// ============================================================================
// USER ROLE SERVICE
// ============================================================================
// API calls for managing role assignments for a user

import { get, post, put, del } from '../client/axiosClient';
import { userRoleEndpoints } from '../endpoints/iam.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  UserRoleAssignmentDto,
  AssignUserRoleRequest,
  UpdateUserRoleRequest,
} from '../types/iam.types';

class UserRoleService {
  async getByUser(
    userId: ID,
    params?: { page?: number; pageSize?: number }
  ): Promise<PaginatedResponse<UserRoleAssignmentDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(userRoleEndpoints.list(userId), params)
      : userRoleEndpoints.list(userId);
    const response = await get<ApiResponse<BackendPagedResult<UserRoleAssignmentDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async assign(userId: ID, data: AssignUserRoleRequest): Promise<ApiResponse<UserRoleAssignmentDto>> {
    const response = await post<ApiResponse<UserRoleAssignmentDto>>(
      userRoleEndpoints.assign(userId),
      data
    );
    return response.data;
  }

  async update(
    userId: ID,
    id: ID,
    data: UpdateUserRoleRequest
  ): Promise<ApiResponse<UserRoleAssignmentDto>> {
    const response = await put<ApiResponse<UserRoleAssignmentDto>>(
      userRoleEndpoints.update(userId, id),
      data
    );
    return response.data;
  }

  async revoke(userId: ID, id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(userRoleEndpoints.revoke(userId, id));
    return response.data;
  }
}

export const userRoleService = new UserRoleService();
export default userRoleService;
